import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, TrendingUp, TrendingDown, Package, Calendar,
  BarChart3, Loader2, Info,
} from 'lucide-react';

// -----------------------------------------------------------------
// DEMO DATA — Forecasts.
// The demand-forecasting integration is not wired yet, so this page
// renders a fixed illustrative dataset that all three commercial
// roles (manufacturer, distributor, shopkeeper) see. Delete this
// block and the "isDemo" banner when the real /analytics/forecasts
// endpoint lands.
// -----------------------------------------------------------------
const DEMO_FORECASTS = [
  { id: 'demo-1', product: 'NPK 15-15-15 Fertilizer', current_stock: 150, predicted_demand: 320, trend: 'up' },
  { id: 'demo-2', product: 'Cypermethrin Insecticide', current_stock: 80,  predicted_demand: 60,  trend: 'down' },
  { id: 'demo-3', product: 'Glyphosate Herbicide',    current_stock: 45,  predicted_demand: 140, trend: 'up' },
  { id: 'demo-4', product: 'Hybrid Maize Seed',       current_stock: 200, predicted_demand: 180, trend: 'stable' },
  { id: 'demo-5', product: 'FARO 44 Rice Seed',       current_stock: 120, predicted_demand: 260, trend: 'up' },
  { id: 'demo-6', product: 'Mancozeb Fungicide',      current_stock: 90,  predicted_demand: 130, trend: 'up' },
  { id: 'demo-7', product: 'Muriate of Potash',       current_stock: 60,  predicted_demand: 55,  trend: 'stable' },
  { id: 'demo-8', product: 'Urea (46% N)',            current_stock: 40,  predicted_demand: 210, trend: 'up' },
];

export default function ForecastsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();  // reserved for real-endpoint personalisation
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [forecasts, setForecasts] = useState([]);
  // isDemo drives the banner + the row test-id prefix so an E2E scenario
  // can prove the placeholder is gone once real forecasting lands.
  const isDemo = true;

  useEffect(() => {
    // Skip the network call entirely — the real /analytics/forecasts
    // endpoint doesn't exist yet, and hitting a 404 to reach the demo
    // dataset made the page feel like an error state on slow devices.
    setForecasts(DEMO_FORECASTS);
    setLoading(false);
    // user is intentionally unused for now; the real fetch will take it.
    void user;
  }, [user]);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      default:
        return <BarChart3 className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStockStatus = (current, predicted) => {
    const ratio = current / predicted;
    // `actionable` marks the two states where the next step is "top up".
    // The chip renders as a button in that case, jumping straight to the
    // Place Order screen with this product prefilled.
    if (ratio < 0.5) return { key: 'lowStock',  color: 'text-red-500 bg-red-50',    actionable: true };
    if (ratio < 0.8) return { key: 'orderSoon', color: 'text-yellow-500 bg-yellow-50', actionable: true };
    return { key: 'adequate', color: 'text-green-500 bg-green-50', actionable: false };
  };

  // No orders on the platform (PR 17) — restocking is arranged
  // directly with a connected supplier, so the low-stock action opens
  // the Suppliers list.
  const goContactSupplier = () => {
    navigate('/suppliers');
  };

  const renderForecasts = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
        </div>
      );
    }
    if (forecasts.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <BarChart3 className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{t('forecasts.empty.title')}</h3>
            <p className="text-muted-foreground text-center">{t('forecasts.empty.subtitle')}</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-3">
        {forecasts.map((forecast) => {
          const status = getStockStatus(forecast.current_stock, forecast.predicted_demand);
          return (
            <Card key={forecast.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-farm-primary/10 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-farm-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{forecast.product}</h3>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {t('forecasts.period')}
                      </p>
                    </div>
                  </div>
                  {getTrendIcon(forecast.trend)}
                </div>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold">{forecast.current_stock}</p>
                    <p className="text-xs text-muted-foreground">{t('forecasts.columns.current')}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-farm-primary">{forecast.predicted_demand}</p>
                    <p className="text-xs text-muted-foreground">{t('forecasts.columns.predicted')}</p>
                  </div>
                  <div>
                    {status.actionable ? (
                      <button
                        type="button"
                        onClick={goContactSupplier}
                        className={`inline-block px-3 py-1.5 rounded-full text-xs font-semibold hover:opacity-80 transition-opacity cursor-pointer ${status.color}`}
                        data-testid={`forecast-contact-supplier-${forecast.id}`}
                        aria-label={t('forecasts.contactSupplierAria', { product: forecast.product, defaultValue: 'Contact a supplier about {{product}}' })}
                      >
                        {t(`forecasts.status.${status.key}`)} · {t('forecasts.contactSupplier', { defaultValue: 'Contact supplier' })}
                      </button>
                    ) : (
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        {t(`forecasts.status.${status.key}`)}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-4 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t('forecasts.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('forecasts.subtitle')}</p>
        </div>
      </div>

      {/* Demo-data banner. Delete along with DEMO_FORECASTS when the
          real /analytics/forecasts endpoint lands. */}
      {isDemo && (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="forecasts-demo-banner"
        >
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>{t('forecasts.demoBanner', {
            defaultValue: 'Demo data — real demand forecasting is coming soon.',
          })}</p>
        </div>
      )}

      {/* Summary Card */}
      <Card className="mb-6 bg-gradient-to-r from-farm-primary to-farm-primary-dark text-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <BarChart3 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-white/80 text-sm">{t('forecasts.summary.badge')}</p>
              <p className="text-xl font-bold">{t('forecasts.summary.body')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {renderForecasts()}
    </div>
  );
}
